import React, { useEffect, useRef, useState } from 'react';
import './user.scss';

import { useDispatch, useSelector, useStore } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  getPaginationOpts,
  getResponseError,
  responseLogWithNotification,
  useFilterForm,
  useUpdatePeriod,
} from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import { getUserData } from '../reducer';
import Loader from '../../../components/base/Loader/Loader';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import Pagination from '../../../components/base/Pagination/Pagination';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import UserDTO from '../../../dto/UserDTO';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import UserForm from './UserForm';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { showGlobalConfirm } from '../../System/actions';
import { userAdditionalColumns, userColumns } from './userColumns';
import {
  deleteUser, loadUserById, loadUserByIdClear, loadUserList,
} from '../actions';
import { USER_MODULE } from '../../../configs/moduleNames';
import { USER_STATUS, USER_STATUS_LIST } from '../../../configs';
import TextInput from '../../../components/fields/TextInput/TextInput';

export default function UserPage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const [selectedRow, setSelectedRow] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const { updatePeriod } = useSelector(getSystemData);

  const store = useStore();
  const {
    userListMeta,
    loadingUserList,
    userList,
    loadingCurrentUser,
    currentUser,
    loadingCreateUser,
    loadingEditUserById,
    loadingDeleteUser,
  } = useSelector(getUserData);
  const { formatMessage } = useIntl();

  const loadUser = id => {
    if (id) {
      dispatch(loadUserById(id))
        .then(() => setShowForm(true))
        .catch(response => errorNotification('Загрузка данных пользователя', getResponseError(response)));
    }
  };

  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[USER_MODULE].userListMeta, opts); // костыль внедрен из за потери контекста в updatePeriod. Он сохранял первую функцию
    const requestData = {
      ...paginationOpts,
      filter: getClearValues(),
    };
    const p1 = dispatch(loadUserList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const {
    getValues, getClearValues, control, setValue,
  } = useFilterForm({ is_active: USER_STATUS.ALL }, loadData, ['user_id']);
  const vid = getValues('user_id');
  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  React.useEffect(() => loadUser(getValues('user_id')), [vid]);
  const DTO = new UserDTO();
  const attributes = DTO.getAttributesByName();

  const handleSelectRow = item => {
    let select = item;
    if (selectedRow && selectedRow.id === item.id) {
      select = null;
    }
    setSelectedRow(select);
  };

  const handleCreate = () => {
    clearCurrentState();
    setShowForm(true);
  };

  const handleEdit = item => event => {
    if (!item) {
      return;
    }
    setValue('user_id', item.id);
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDelete = item => event => {
    if (!item.id) {
      errorNotification('Ошибка', 'Нет ID');
    }
    dispatch(showGlobalConfirm({
      confirmMessage: `Удаление пользователя # ${item.id}`,
      confirmButtonText: 'Удалить',
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteUser(item.id)).then(() => {
        if (selectedRow === item.id) {
          setSelectedRow(null);
        }
        loadData();
        successNotification('Удаление пользователя', 'Удаление прошло успешно');
      }).catch(response => {
        errorNotification('Удаление пользователя', getResponseError(response));
      });
    }).catch(() => {});
    event.preventDefault();
    event.stopPropagation();
    return false;
  };

  const copyLink = item => event => {
    navigator.clipboard.writeText(`${window.location.href}?user_id=${item.id}`);
    successNotification('Копирование ссылки на пользователя', 'Ссылка на просмотр скопирована.');
  };

  const handleClose = () => {
    setShowForm(false);
    setValue('user_id', '');
    clearCurrentState();
  };

  const clearCurrentState = () => {
    dispatch(loadUserByIdClear()).catch(responseLogWithNotification);
  };

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={(
        <div className="more-wrapper">
          <Button variant="link" className="w-100 more-button" onClick={copyLink(item)}>
            Копировать ссылку
          </Button>
          <Button variant="link" className="w-100 more-button" onClick={handleEdit(item)}>
            Просмотреть
          </Button>
          <Button variant="link" className="w-100 more-button delete" onClick={handleDelete(item)}>
            Удалить
          </Button>
        </div>
      )}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  const rowClassFormatter = item => {
    const classes = ['clickable-table-row'];
    if (selectedRow && item.id === selectedRow.id) {
      classes.push('selected');
    }
    return classes.join(' ');
  };

  return (
    <div className="page application-page">
      <Loader loading={loadingUserList || loadingCurrentUser || loadingDeleteUser || loadingCreateUser || loadingEditUserById} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.user' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={attributes.is_active}
            usePortal
            placeholder="Статус"
            className="no-border"
            options={USER_STATUS_LIST}
          />
          <FieldDTO
            control={control}
            component={TextInput}
            dtoObject={attributes.username}
            placeholder="Имя пользователя"
            className="no-border"
          />
          <div className="filter-sep" />
          <Button variant="second" onClick={handleCreate}>
            <Icons
              customIcon
              width={24}
              height={24}
              icon="plus"
            />
          </Button>
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper frame">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid
                onClickByRow={handleSelectRow}
                onDoubleClickByRow={(item, event) => handleEdit(item)(event)}
                rowClassFormatter={rowClassFormatter}
                data={userList}
                columns={userColumns}
                additionalColumns={userAdditionalColumns}
                name="user-list"
                settingsFormatter={moreFormatter}
              />
            </div>
          </div>
          <Pagination meta={userListMeta} list={userList} handleChangeMeta={loadData} />
        </div>
      </div>
      {showForm ? (
        <UserForm
          currentUser={currentUser}
          loadData={loadData}
          onClose={handleClose}
          loadUser={loadUser}
        />
      ) : null}
    </div>
  );
}
